import React, { useState, useEffect } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  ScrollView,
  Platform,
  Image,
  Switch } from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import { ParkingCircle, Accessibility, Wifi, Shirt, Coffee, CreditCard, Banknote, Smartphone, FileText, Image as ImageIcon, Clock, MapPin, Sparkles, Store, type LucideIcon } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import LocationPicker from '../components/LocationPicker'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StoreSettings'>
  route: RouteProp<RootStackParamList, 'StoreSettings'>
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const AMENITIES: { key: string; icon: LucideIcon; labelKey: string }[] = [
  { key: 'parking', icon: ParkingCircle, labelKey: 'parking' },
  { key: 'wheelchair', icon: Accessibility, labelKey: 'wheelchair_accessible' },
  { key: 'wifi', icon: Wifi, labelKey: 'free_wifi' },
  { key: 'fitting_room', icon: Shirt, labelKey: 'fitting_room' },
  { key: 'cafe', icon: Coffee, labelKey: 'cafe' },
  { key: 'card_payment', icon: CreditCard, labelKey: 'card_payment' },
  { key: 'cash_payment', icon: Banknote, labelKey: 'cash_payment' },
  { key: 'mobilepay', icon: Smartphone, labelKey: 'mobilepay' },
]

const SECTION_ICONS: Record<string, LucideIcon> = {
  basic: FileText,
  images: ImageIcon,
  hours: Clock,
  contact: MapPin,
  amenities: Sparkles }

interface OpeningHours {
  [key: string]: { open: string; close: string; closed: boolean }
}

interface StoreData {
  name: string
  storeType: string | null
  description: string
  addressStreet: string
  addressCity: string
  addressPostalCode: string
  phone: string
  email: string
  websiteUrl: string
  logoUrl: string | null
  coverImageUrl: string | null
  openingHours: OpeningHours
  amenities: string[]
  acceptsOffers: boolean
  commissionPercentage: string
  latitude: number | null
  longitude: number | null
}

const defaultOpeningHours: OpeningHours = {
  monday: { open: '10:00', close: '18:00', closed: false },
  tuesday: { open: '10:00', close: '18:00', closed: false },
  wednesday: { open: '10:00', close: '18:00', closed: false },
  thursday: { open: '10:00', close: '18:00', closed: false },
  friday: { open: '10:00', close: '18:00', closed: false },
  saturday: { open: '10:00', close: '16:00', closed: false },
  sunday: { open: '', close: '', closed: true } }

export default function StoreSettingsScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t } = useLanguage()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('basic')

  const [data, setData] = useState<StoreData>({
    name: '',
    storeType: null,
    description: '',
    addressStreet: '',
    addressCity: '',
    addressPostalCode: '',
    phone: '',
    email: '',
    websiteUrl: '',
    logoUrl: null,
    coverImageUrl: null,
    openingHours: defaultOpeningHours,
    amenities: [],
    acceptsOffers: true,
    commissionPercentage: '30',
    latitude: null,
    longitude: null })

  useEffect(() => {
    fetchStore()
  }, [storeId])

  const fetchStore = async () => {
    try {
      const { data: store, error } = await supabase
        .from('flea_markets')
        .select('*')
        .eq('id', storeId)
        .single()

      if (error) throw error

      // Fetch location separately via RPC (PostGIS geography isn't returned by default select)
      let lat: number | null = null
      let lng: number | null = null
      try {
        const { data: locData } = await supabase.rpc('get_store_location', { p_store_id: storeId })
        if (locData) {
          lat = locData.lat
          lng = locData.lng
        }
      } catch (e) {
        console.warn('Could not fetch store location:', e)
      }

      setData({
        name: store.name || '',
        storeType: store.store_type || null,
        description: store.description || '',
        addressStreet: store.address_street || '',
        addressCity: store.address_city || '',
        addressPostalCode: store.address_postal_code || '',
        phone: store.phone || '',
        email: store.email || '',
        websiteUrl: store.website_url || '',
        logoUrl: store.logo_url || null,
        coverImageUrl: store.cover_image_url || null,
        openingHours: store.opening_hours || defaultOpeningHours,
        amenities: store.settings?.amenities || [],
        acceptsOffers: store.settings?.accepts_offers ?? true,
        commissionPercentage: store.commission_percentage?.toString() || '30',
        latitude: lat,
        longitude: lng })
    } catch (error) {
      console.error('Error fetching store:', error)
      showAlert(t('error') || 'Error', 'Failed to load store')
    } finally {
      setLoading(false)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        onConfirm()
      }
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', style: 'destructive', onPress: onConfirm },
      ])
    }
  }

  const pickImage = async (type: 'logo' | 'cover') => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: type === 'logo' ? [1, 1] : [16, 9],
        quality: 0.8 })

      if (!result.canceled && result.assets[0]) {
        await uploadImage(result.assets[0].uri, type)
      }
    } catch (error) {
      console.error('Error picking image:', error)
      showAlert(t('error') || 'Error', 'Failed to pick image')
    }
  }

  const uploadImage = async (uri: string, type: 'logo' | 'cover') => {
    if (type === 'logo') setUploadingLogo(true)
    else setUploadingCover(true)

    try {
      const response = await fetch(uri)
      const blob = await response.blob()
      const fileName = `${storeId}/${type}-${Date.now()}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('store-images')
        .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage.from('store-images').getPublicUrl(fileName)

      if (type === 'logo') {
        setData(prev => ({ ...prev, logoUrl: urlData.publicUrl }))
      } else {
        setData(prev => ({ ...prev, coverImageUrl: urlData.publicUrl }))
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      showAlert(t('error') || 'Error', 'Failed to upload image')
    } finally {
      if (type === 'logo') setUploadingLogo(false)
      else setUploadingCover(false)
    }
  }

  const updateOpeningHours = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value } } }))
  }

  const toggleAmenity = (amenityKey: string) => {
    setData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityKey)
        ? prev.amenities.filter(a => a !== amenityKey)
        : [...prev.amenities, amenityKey] }))
  }

  const saveStore = async () => {
    if (!data.name.trim()) {
      showAlert(t('error') || 'Error', t('enter_store_name') || 'Please enter a store name')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('flea_markets')
        .update({
          name: data.name.trim(),
          store_type: data.storeType,
          description: data.description.trim() || null,
          address_street: data.addressStreet.trim() || null,
          address_city: data.addressCity.trim() || null,
          address_postal_code: data.addressPostalCode.trim() || null,
          phone: data.phone.trim() || null,
          email: data.email.trim() || null,
          website_url: data.websiteUrl.trim() || null,
          logo_url: data.logoUrl,
          cover_image_url: data.coverImageUrl,
          opening_hours: data.openingHours,
          settings: { amenities: data.amenities, accepts_offers: data.acceptsOffers },
          commission_percentage: parseFloat(data.commissionPercentage) || 0 })
        .eq('id', storeId)

      // Save location via RPC if we have coordinates
      if (data.latitude != null && data.longitude != null) {
        await supabase.rpc('set_store_location', {
          p_store_id: storeId,
          p_lng: data.longitude,
          p_lat: data.latitude })
      }

      if (error) throw error

      showAlert(t('success') || 'Success', t('store_updated') || 'Store updated successfully!')
    } catch (error: any) {
      console.error('Update store error:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to update store')
    } finally {
      setSaving(false)
    }
  }

  const deleteStore = async () => {
    confirmAction(
      t('delete') || 'Delete',
      t('delete_store_confirm') || 'Are you sure you want to delete this store? All products will also be deleted.',
      async () => {
        setDeleting(true)
        try {
          const { error: productsError } = await supabase
            .from('products')
            .delete()
            .eq('flea_market_id', storeId)

          if (productsError) throw productsError

          const { error: staffError } = await supabase
            .from('flea_market_staff')
            .delete()
            .eq('flea_market_id', storeId)

          if (staffError) throw staffError

          const { error } = await supabase
            .from('flea_markets')
            .delete()
            .eq('id', storeId)

          if (error) throw error

          showAlert(t('success') || 'Success', t('store_deleted') || 'Store deleted')
          navigation.navigate('MerchantStoreSelect')
        } catch (error: any) {
          console.error('Delete store error:', error)
          showAlert(t('error') || 'Error', error.message || 'Failed to delete store')
        } finally {
          setDeleting(false)
        }
      }
    )
  }

  const renderSectionTab = (key: string, label: string) => {
    const IconComponent = SECTION_ICONS[key] || FileText
    const isActive = activeSection === key
    return (
      <TouchableOpacity
        key={key}
        style={[styles.sectionTab, isActive && styles.sectionTabActive]}
        onPress={() => setActiveSection(key)}
      >
        <IconComponent size={16} color={isActive ? '#fff' : theme.colors.text.secondary} />
        <Text style={[styles.sectionTabText, isActive && styles.sectionTabTextActive]}>
          {label}
        </Text>
      </TouchableOpacity>
    )
  }

  const renderBasicSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('store_name') || 'Store Name'} *</Text>
        <TextInput
          style={styles.input}
          value={data.name}
          onChangeText={(text) => setData(prev => ({ ...prev, name: text }))}
          placeholder={t('store_name_placeholder') || 'e.g. Vintage Treasures Helsinki'}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('description') || 'Description'}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={data.description}
          onChangeText={(text) => setData(prev => ({ ...prev, description: text }))}
          placeholder={t('store_description_placeholder') || 'Describe your store...'}
          placeholderTextColor={theme.colors.text.secondary}
          multiline
          numberOfLines={4}
        />
      </View>

      {data.storeType === 'consignment_store' && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('commission_percentage') || 'Commission %'}</Text>
          <TextInput
            style={styles.input}
            value={data.commissionPercentage}
            onChangeText={(text) => setData(prev => ({ ...prev, commissionPercentage: text }))}
            placeholder="30"
            placeholderTextColor={theme.colors.text.secondary}
            keyboardType="numeric"
          />
          <Text style={styles.hint}>
            {t('commission_hint') || 'Percentage kept by the store on consignment sales'}
          </Text>
        </View>
      )}
    </View>
  )

  const renderImagesSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('store_logo') || 'Store Logo'}</Text>
        <TouchableOpacity style={styles.imageUploadBox} onPress={() => pickImage('logo')} disabled={uploadingLogo}>
          {uploadingLogo ? (
            <Spinner size="small" />
          ) : data.logoUrl ? (
            <Image source={{ uri: data.logoUrl }} style={styles.logoPreview} />
          ) : (
            <View style={styles.imagePlaceholder}>
              <View style={styles.imagePlaceholderIconContainer}>
                <ImageIcon size={24} color={theme.colors.text.secondary} />
              </View>
              <Text style={styles.imagePlaceholderText}>{t('tap_to_upload') || 'Tap to upload'}</Text>
            </View>
          )}
        </TouchableOpacity>
        <Text style={styles.hint}>{t('logo_hint') || 'Square image recommended (1:1)'}</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('cover_image') || 'Cover Image'}</Text>
        <TouchableOpacity style={styles.coverUploadBox} onPress={() => pickImage('cover')} disabled={uploadingCover}>
          {uploadingCover ? (
            <Spinner size="small" />
          ) : data.coverImageUrl ? (
            <Image source={{ uri: data.coverImageUrl }} style={styles.coverPreview} />
          ) : (
            <View style={styles.imagePlaceholder}>
              <View style={styles.imagePlaceholderIconContainer}>
                <Store size={24} color={theme.colors.text.secondary} />
              </View>
              <Text style={styles.imagePlaceholderText}>{t('tap_to_upload') || 'Tap to upload'}</Text>
            </View>
          )}
        </TouchableOpacity>
        <Text style={styles.hint}>{t('cover_hint') || 'Wide image recommended (16:9)'}</Text>
      </View>
    </View>
  )

  const renderHoursSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionDescription}>
        {t('opening_hours_desc') || 'Set your store opening hours for each day of the week'}
      </Text>
      {WEEKDAYS.map((day) => (
        <View key={day} style={styles.hoursRow}>
          <View style={styles.dayLabel}>
            <Text style={styles.dayText}>{t(day) || day}</Text>
          </View>
          <View style={styles.hoursInputs}>
            {data.openingHours[day]?.closed ? (
              <Text style={styles.closedText}>{t('closed') || 'Closed'}</Text>
            ) : (
              <>
                <TextInput
                  style={styles.timeInput}
                  value={data.openingHours[day]?.open || ''}
                  onChangeText={(text) => updateOpeningHours(day, 'open', text)}
                  placeholder="10:00"
                  placeholderTextColor={theme.colors.text.secondary}
                />
                <Text style={styles.timeSeparator}>-</Text>
                <TextInput
                  style={styles.timeInput}
                  value={data.openingHours[day]?.close || ''}
                  onChangeText={(text) => updateOpeningHours(day, 'close', text)}
                  placeholder="18:00"
                  placeholderTextColor={theme.colors.text.secondary}
                />
              </>
            )}
          </View>
          <Switch
            value={!data.openingHours[day]?.closed}
            onValueChange={(value) => updateOpeningHours(day, 'closed', !value)}
            trackColor={{ false: theme.colors.ui.borderLight, true: theme.colors.merchant.light }}
            thumbColor={data.openingHours[day]?.closed ? theme.colors.text.secondary : theme.colors.merchant.primary}
          />
        </View>
      ))}
    </View>
  )

  const renderContactSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('address_street') || 'Street Address'}</Text>
        <TextInput
          style={styles.input}
          value={data.addressStreet}
          onChangeText={(text) => setData(prev => ({ ...prev, addressStreet: text }))}
          placeholder={t('address_street_placeholder') || 'e.g. Mannerheimintie 10'}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      <View style={styles.rowInputs}>
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={styles.label}>{t('postal_code') || 'Postal Code'}</Text>
          <TextInput
            style={styles.input}
            value={data.addressPostalCode}
            onChangeText={(text) => setData(prev => ({ ...prev, addressPostalCode: text }))}
            placeholder="00100"
            placeholderTextColor={theme.colors.text.secondary}
          />
        </View>
        <View style={[styles.inputGroup, { flex: 2, marginLeft: 12 }]}>
          <Text style={styles.label}>{t('city') || 'City'}</Text>
          <TextInput
            style={styles.input}
            value={data.addressCity}
            onChangeText={(text) => setData(prev => ({ ...prev, addressCity: text }))}
            placeholder={t('city_placeholder') || 'Helsinki'}
            placeholderTextColor={theme.colors.text.secondary}
          />
        </View>
      </View>

      {/* Location Pin Picker */}
      {(data.addressStreet.trim() || data.addressCity.trim()) && (
        <LocationPicker
          address={data.addressStreet}
          city={data.addressCity}
          initialLat={data.latitude}
          initialLng={data.longitude}
          onLocationChange={(lat, lng) => {
            setData(prev => ({ ...prev, latitude: lat, longitude: lng }))
          }}
        />
      )}

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('phone') || 'Phone'}</Text>
        <TextInput
          style={styles.input}
          value={data.phone}
          onChangeText={(text) => setData(prev => ({ ...prev, phone: text }))}
          placeholder="+358 40 123 4567"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="phone-pad"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('email') || 'Email'}</Text>
        <TextInput
          style={styles.input}
          value={data.email}
          onChangeText={(text) => setData(prev => ({ ...prev, email: text }))}
          placeholder="store@example.com"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="email-address"
          autoCapitalize="none"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('website') || 'Website'}</Text>
        <TextInput
          style={styles.input}
          value={data.websiteUrl}
          onChangeText={(text) => setData(prev => ({ ...prev, websiteUrl: text }))}
          placeholder="https://www.example.com"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="url"
          autoCapitalize="none"
        />
      </View>
    </View>
  )

  const renderAmenitiesSection = () => (
    <View style={styles.section}>
      {/* Incoming offers toggle */}
      <View style={styles.toggleRow}>
        <View style={styles.toggleInfo}>
          <Text style={styles.toggleLabel}>{t('accepts_offers') || 'Incoming offers'}</Text>
          <Text style={styles.toggleDesc}>{t('accepts_offers_desc') || 'Allow sellers to submit product offers to your store'}</Text>
        </View>
        <Switch
          value={data.acceptsOffers}
          onValueChange={(v) => setData(prev => ({ ...prev, acceptsOffers: v }))}
          trackColor={{ false: '#E0E0E0', true: theme.colors.merchant.primaryLight }}
          thumbColor={data.acceptsOffers ? theme.colors.merchant.primary : '#fff'}
        />
      </View>
      <View style={styles.sectionDivider} />
      <Text style={styles.sectionDescription}>
        {t('amenities_desc') || 'Select the amenities available at your store'}
      </Text>
      <View style={styles.amenitiesGrid}>
        {AMENITIES.map((amenity) => {
          const isSelected = data.amenities.includes(amenity.key)
          const IconComponent = amenity.icon
          return (
            <TouchableOpacity
              key={amenity.key}
              style={[styles.amenityItem, isSelected && styles.amenityItemSelected]}
              onPress={() => toggleAmenity(amenity.key)}
            >
              <View style={styles.amenityIconContainer}>
                <IconComponent size={20} color={isSelected ? theme.colors.merchant.primary : theme.colors.text.secondary} />
              </View>
              <Text style={[styles.amenityLabel, isSelected && styles.amenityLabelSelected]}>
                {t(amenity.labelKey) || amenity.labelKey}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('store_settings') || 'Store Settings'}</Text>
      </View>

      {/* Section Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsContainer} contentContainerStyle={styles.tabsContent}>
        {renderSectionTab('basic', t('basic_info') || 'Basic')}
        {renderSectionTab('images', t('images') || 'Images')}
        {renderSectionTab('hours', t('opening_hours') || 'Hours')}
        {renderSectionTab('contact', t('contact') || 'Contact')}
        {renderSectionTab('amenities', t('amenities') || 'Amenities')}
      </ScrollView>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {activeSection === 'basic' && renderBasicSection()}
        {activeSection === 'images' && renderImagesSection()}
        {activeSection === 'hours' && renderHoursSection()}
        {activeSection === 'contact' && renderContactSection()}
        {activeSection === 'amenities' && renderAmenitiesSection()}

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.buttonDisabled]}
          onPress={saveStore}
          disabled={saving}
        >
          {saving ? (
            <Spinner color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>{t('save_changes') || 'Save Changes'}</Text>
          )}
        </TouchableOpacity>

        {/* Delete Link - only in basic section */}
        {activeSection === 'basic' && (
          <TouchableOpacity
            style={styles.deleteLink}
            onPress={deleteStore}
            disabled={deleting}
          >
            {deleting ? (
              <Spinner size="small" />
            ) : (
              <Text style={styles.deleteLinkText}>{t('delete_store') || 'Delete Store'}</Text>
            )}
          </TouchableOpacity>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  tabsContainer: {
    backgroundColor: theme.colors.background.card,
    maxHeight: 60,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  tabsContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8 },
  sectionTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.background.secondary,
    marginRight: 8 },
  sectionTabActive: {
    backgroundColor: theme.colors.merchant.primary },
  sectionTabText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  sectionTabTextActive: {
    color: '#fff' },
  content: {
    flex: 1,
    padding: 16 },
  section: {
    marginBottom: 16 },
  sectionDescription: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 16 },
  inputGroup: {
    marginBottom: 20 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  textArea: {
    height: 100,
    textAlignVertical: 'top' },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6 },
  rowInputs: {
    flexDirection: 'row' },
  imageUploadBox: {
    width: 120,
    height: 120,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.card,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden' },
  coverUploadBox: {
    width: '100%',
    height: 160,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.card,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden' },
  logoPreview: {
    width: '100%',
    height: '100%' },
  coverPreview: {
    width: '100%',
    height: '100%' },
  imagePlaceholder: {
    alignItems: 'center' },
  imagePlaceholderIconContainer: {
    marginBottom: 8,
    opacity: 0.5 },
  imagePlaceholderText: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  hoursRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md },
  dayLabel: {
    width: 80 },
  dayText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary,
    textTransform: 'capitalize' },
  hoursInputs: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center' },
  timeInput: {
    width: 70,
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.sm,
    padding: 8,
    fontSize: 14,
    textAlign: 'center',
    color: theme.colors.text.primary },
  timeSeparator: {
    marginHorizontal: 8,
    fontSize: 16,
    color: theme.colors.text.secondary },
  closedText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    fontStyle: 'italic' },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4 },
  toggleInfo: {
    flex: 1,
    marginRight: 16 },
  toggleLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.primary },
  toggleDesc: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  sectionDivider: {
    height: 1,
    backgroundColor: '#F0EDEB',
    marginVertical: 16 },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10 },
  amenityItem: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.borderLight },
  amenityItemSelected: {
    borderColor: theme.colors.merchant.primary,
    backgroundColor: theme.colors.merchant.background },
  amenityIconContainer: {
    marginRight: 10 },
  amenityLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    flex: 1 },
  amenityLabelSelected: {
    color: theme.colors.merchant.primary,
    fontWeight: '600' },
  saveButton: {
    backgroundColor: theme.colors.merchant.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center',
    marginTop: 24,
    ...theme.shadows.medium },
  buttonDisabled: {
    opacity: 0.6 },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600' },
  deleteLink: {
    alignItems: 'center',
    marginTop: 32,
    padding: 12 },
  deleteLinkText: {
    color: theme.colors.text.secondary,
    fontSize: 14,
    textDecorationLine: 'underline' },
  bottomSpacer: {
    height: 100 } })
